/**
 * useAttentionCenter — Aggregates every operational stream that the Owner
 * needs to attend to. Zero new backend surface: reuses existing endpoints.
 *
 * Streams:
 *   • Readiness blockers          GET /owner/readiness/launch-blockers
 *   • Security lockouts           GET /owner/security/events (filtered)
 *   • SOC alerts                  GET /internal/soc/alerts?status=active
 *   • Infrastructure warn/fail    GET /owner/readiness/scorecard (infra cats)
 *   • Provider health             GET /internal/providers/health
 *   • Notification actions        GET /owner/notifications (critical/warn unread)
 *
 * Returns aggregated items plus derived counts (severity + source) so that
 * the Attention Center page and the sidebar badge can share one shape.
 *
 * Options:
 *   pollMs   number|null   — auto-refresh cadence (default null = manual)
 *   enabled  boolean       — pause fetching entirely (default true)
 */
import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";

const SEVERITY_RANK = { critical: 0, high: 0, warning: 1, warn: 1, medium: 1, info: 2, low: 2 };
export const rankOf = (i) => (SEVERITY_RANK[(i.severity || "").toLowerCase()] ?? 3);

/** Impact tier heuristic derived from severity + source. */
function impactOf(item) {
  const r = rankOf(item);
  const src = (item.source || "").toLowerCase();
  if (r === 0) {
    if (["readiness", "billing", "security", "soc"].includes(src)) return "high";
    return "high";
  }
  if (r === 1) {
    if (["security", "soc", "billing"].includes(src)) return "medium";
    return "medium";
  }
  return "low";
}

/** Coerce a created_at value across possible sources. */
function pickCreatedAt(raw) {
  const c = raw?.created_at || raw?.first_seen_at || raw?.timestamp || raw?.updated_at;
  return c ? new Date(c).toISOString() : null;
}

export function useAttentionCenter({ pollMs = null, enabled = true } = {}) {
  const [items, setItems]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [lastFetchedAt, setLastFetchedAt] = useState(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const refresh = useCallback(async () => {
    if (!enabled) return;
    setLoading(true); setError(null);
    const safe = async (fn) => { try { return await fn(); } catch { return null; } };

    const [blockers, secEvents, socAlerts, scorecard, provHealth, notifs] = await Promise.all([
      safe(() => api.get("/owner/readiness/launch-blockers?limit=50").then(r => r.data)),
      safe(() => api.get("/owner/security/events?limit=100").then(r => r.data)),
      safe(() => api.get("/internal/soc/alerts?status=active").then(r => r.data)
                    .catch(() => api.get("/internal/soc/alerts").then(r => r.data))),
      safe(() => api.get("/owner/readiness/scorecard").then(r => r.data)),
      safe(() => api.get("/internal/providers/health").then(r => r.data)),
      safe(() => api.get("/owner/notifications?limit=100").then(r => r.data)),
    ]);

    const all = [];

    for (const b of (blockers?.blockers || [])) {
      all.push({
        id: `readiness:${b.id}`, source: "Readiness", category: "readiness",
        severity: b.severity || "critical",
        title: b.friendly || b.label,
        description: b.detail || b.recommended_fix || "",
        created_at: pickCreatedAt(b),
        frequency: 1,
        action_url: "/owner/readiness",
        auto_fixable: !!b.fixable,
        endpoint: b.fixable ? `/owner/readiness/checks/${b.id}/fix` : null,
        method: "POST",
        action_label: b.fixable ? "Fix" : "Manual",
        runbook: b.recommended_fix || null,
        raw: b,
      });
    }

    for (const e of (secEvents?.items || [])) {
      if (!/lockout/i.test(e.event_type || "")) continue;
      const isCustomer = /customer/i.test(e.event_type);
      all.push({
        id: `security:${e.id}`, source: "Security", category: "security",
        severity: e.severity || "warning",
        title: `${isCustomer ? "User" : "Staff"} lockout — ${e.target_email || e.target_id}`,
        description: e.reason || e.event_type,
        created_at: pickCreatedAt(e),
        frequency: 1,
        action_url: `/owner/security?target=${e.target_id}`,
        auto_fixable: !!e.target_id,
        endpoint: e.target_id ? `/owner/security/remediate/${isCustomer ? "unlock-user" : "unlock-staff"}/${e.target_id}` : null,
        method: "POST",
        action_label: e.target_id ? "Unlock" : "View",
        runbook: `Unlock the account. Idempotent — a re-lockout will re-appear.`,
        raw: e,
      });
    }

    const alertArr = Array.isArray(socAlerts) ? socAlerts : (socAlerts?.items || socAlerts?.alerts || []);
    for (const a of alertArr) {
      if (a.status === "resolved") continue;
      all.push({
        id: `soc:${a.id}`, source: "SOC", category: "soc",
        severity: a.severity || "warning",
        title: a.rule_name || "SOC alert",
        description: `Events: ${a.grouped_count || 1}`,
        created_at: pickCreatedAt(a),
        frequency: Number(a.grouped_count) || 1,
        action_url: "/owner/soc",
        auto_fixable: true,
        endpoint: `/internal/soc/alerts/${a.id}/resolve`,
        method: "POST",
        action_label: "Resolve",
        runbook: a.runbook || null,
        raw: a,
      });
    }

    for (const cat of (scorecard?.categories || [])) {
      if (!/infra|storage|backup|provider/i.test(cat.id || cat.name || "")) continue;
      for (const c of (cat.checks || [])) {
        if (c.status !== "warn" && c.status !== "fail") continue;
        all.push({
          id: `infra:${c.id}`, source: "Infrastructure", category: "infra",
          severity: c.status === "fail" ? "critical" : "warning",
          title: c.label,
          description: c.detail || c.why || "",
          created_at: null,
          frequency: 1,
          action_url: "/owner/readiness",
          auto_fixable: !!c.fixable,
          endpoint: c.fixable ? `/owner/readiness/checks/${c.id}/fix` : null,
          method: "POST",
          action_label: c.fixable ? "Fix" : "View",
          runbook: c.recommended_fix || null,
          raw: c,
        });
      }
    }

    if (Array.isArray(provHealth)) {
      for (const p of provHealth) {
        if (p.status !== "unhealthy" && p.status !== "degraded" && p.status !== "down") continue;
        all.push({
          id: `provider:${p.service || p.name}`, source: "Provider", category: "provider",
          severity: p.status === "down" ? "critical" : "warning",
          title: `${p.service || p.name} — ${p.status}`,
          description: p.error || p.detail || "",
          created_at: pickCreatedAt(p),
          frequency: 1,
          action_url: "/owner/providers",
          auto_fixable: false,
          endpoint: null,
          action_label: "Investigate",
          runbook: "Open Providers, verify credentials, run Health Check.",
          raw: p,
        });
      }
    }

    const groups = notifs?.groups || {};
    for (const arr of Object.values(groups)) {
      for (const n of arr) {
        if (n.read) continue;
        if (n.level !== "critical" && n.level !== "warning") continue;
        if (n.category === "security" || n.category === "launch") continue;
        all.push({
          id: `notif:${n.id}`,
          source: n.category === "billing" ? "Billing" : (n.category === "infrastructure" ? "Infrastructure" : "Notification"),
          category: n.category,
          severity: n.level,
          title: n.title,
          description: n.description || "",
          created_at: pickCreatedAt(n),
          frequency: 1,
          action_url: n.action_url || "/owner/notifications",
          auto_fixable: false,
          endpoint: null,
          action_label: "View",
          runbook: null,
          raw: n,
        });
      }
    }

    // Add derived impact tier
    for (const it of all) it.impact = impactOf(it);

    // Rank: severity, then source stable
    all.sort((a, b) => rankOf(a) - rankOf(b) || a.source.localeCompare(b.source));

    if (!mounted.current) return;
    setItems(all);
    setLastFetchedAt(new Date().toISOString());
    setLoading(false);
  }, [enabled]);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    if (!pollMs || !enabled) return undefined;
    const t = setInterval(refresh, pollMs);
    return () => clearInterval(t);
  }, [pollMs, enabled, refresh]);

  // Derived counts (severity + source)
  const counts = {
    total:    items.length,
    critical: items.filter((i) => rankOf(i) === 0).length,
    warning:  items.filter((i) => rankOf(i) === 1).length,
    info:     items.filter((i) => rankOf(i) === 2).length,
    fixable:  items.filter((i) => i.auto_fixable).length,
    manual:   items.filter((i) => !i.auto_fixable).length,
    bySource: items.reduce((m, i) => { m[i.source] = (m[i.source] || 0) + 1; return m; }, {}),
  };

  // Platform health score: 100 - (critical × 5) - (warning × 2), clamped to [0, 100].
  counts.healthScore = Math.max(0, Math.min(100, 100 - counts.critical * 5 - counts.warning * 2));

  return { items, counts, loading, error, refresh, lastFetchedAt };
}
